import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Save, Trash2, Music, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyTrack = (inst) => ({
  instrument_id: inst?.id || '',
  instrument_name: inst?.name || '',
  audio_url: '',
  video_slow_url: '',
  video_medium_url: '',
  video_fast_url: '',
  bpm: 100,
  time_signature: '4/4',
  description: '',
  is_published: true,
});

export default function AdminBackingTracks() {
  const [instruments, setInstruments] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id or 'new'
  const [drafts, setDrafts] = useState({}); // instrumentId -> track object

  const authHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [instRes, trackRes] = await Promise.all([
        axios.get(`${API}/api/instrument-lessons`),
        axios.get(`${API}/api/backing-tracks/all`, { headers: authHeaders() }),
      ]);
      const instList = instRes.data || [];
      const tList = trackRes.data || [];
      setInstruments(instList);
      setTracks(tList);
      // Seed drafts: existing track OR blank for each instrument
      const d = {};
      instList.forEach(inst => {
        const existing = tList.find(t => t.instrument_id === inst.id);
        d[inst.id] = existing ? { ...existing } : emptyTrack(inst);
      });
      setDrafts(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (instId, key, val) => {
    setDrafts(prev => ({ ...prev, [instId]: { ...prev[instId], [key]: val } }));
  };

  const save = async (inst) => {
    const draft = drafts[inst.id];
    setSaving(inst.id);
    try {
      if (draft.id) {
        await axios.patch(`${API}/api/backing-tracks/${draft.id}`, draft, { headers: authHeaders() });
      } else {
        const res = await axios.post(`${API}/api/backing-tracks`, draft, { headers: authHeaders() });
        setDrafts(prev => ({ ...prev, [inst.id]: res.data }));
      }
      toast.success(`Saved backing track for ${inst.name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const remove = async (inst) => {
    const draft = drafts[inst.id];
    if (!draft?.id) {
      // reset local draft
      setDrafts(prev => ({ ...prev, [inst.id]: emptyTrack(inst) }));
      return;
    }
    if (!window.confirm(`Delete backing track for ${inst.name}?`)) return;
    try {
      await axios.delete(`${API}/api/backing-tracks/${draft.id}`, { headers: authHeaders() });
      toast.success('Deleted');
      setDrafts(prev => ({ ...prev, [inst.id]: emptyTrack(inst) }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin" className="p-2 rounded-lg bg-stone-800 hover:bg-stone-700">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-black">Play Along · Backing Tracks (Step 2)</h1>
            <p className="text-sm text-muted-foreground">One backing track per instrument. Shows on <Link to="/play-along#step-2" className="underline text-primary">/play-along Step 2</Link>.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : instruments.length === 0 ? (
          <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6 text-center">
            <Music className="w-10 h-10 mx-auto text-primary/60 mb-3" />
            <p>Add Step 1 instrument lessons first — they define which backing tracks you can upload.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {instruments.map(inst => {
              const d = drafts[inst.id] || emptyTrack(inst);
              return (
                <div key={inst.id} className="bg-stone-900 border border-stone-800 rounded-2xl p-5">
                  <div className="flex items-center gap-4 mb-4">
                    {inst.image ? <img loading="lazy" decoding="async" src={inst.image} alt={inst.name} className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-stone-800 flex items-center justify-center"><Music className="text-primary/60" /></div>}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{inst.name}</h3>
                      <p className="text-xs text-muted-foreground">{d.id ? `Track saved (id ${d.id.slice(-6)})` : 'No track yet'}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => save(inst)}
                        disabled={saving === inst.id}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid={`save-track-${inst.id}`}
                      >
                        <Save className="w-4 h-4" /> {d.id ? 'Update' : 'Create'}
                      </button>
                      <button
                        onClick={() => remove(inst)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
                        title="Delete track"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="text-sm">
                      <span className="block text-xs text-muted-foreground mb-1">Audio loop URL (WAV / MP3)</span>
                      <input value={d.audio_url || ''} onChange={e => setField(inst.id, 'audio_url', e.target.value)} className="w-full px-3 py-2 bg-stone-800 rounded-lg" placeholder="https://…" />
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {['slow', 'medium', 'fast'].map(t => (
                        <label key={t} className="text-sm">
                          <span className="block text-xs text-muted-foreground mb-1 capitalize">{t} tempo video</span>
                          <input value={d[`video_${t}_url`] || ''} onChange={e => setField(inst.id, `video_${t}_url`, e.target.value)} className="w-full px-3 py-2 bg-stone-800 rounded-lg" placeholder="Vimeo / YouTube / MP4" />
                        </label>
                      ))}
                    </div>
                    <label className="text-sm">
                      <span className="block text-xs text-muted-foreground mb-1">BPM</span>
                      <input type="number" value={d.bpm || 100} onChange={e => setField(inst.id, 'bpm', parseInt(e.target.value || '0', 10))} className="w-full px-3 py-2 bg-stone-800 rounded-lg" />
                    </label>
                    <label className="text-sm">
                      <span className="block text-xs text-muted-foreground mb-1">Time signature</span>
                      <input value={d.time_signature || '4/4'} onChange={e => setField(inst.id, 'time_signature', e.target.value)} className="w-full px-3 py-2 bg-stone-800 rounded-lg" />
                    </label>
                    <label className="md:col-span-2 text-sm">
                      <span className="block text-xs text-muted-foreground mb-1">Description (optional)</span>
                      <input value={d.description || ''} onChange={e => setField(inst.id, 'description', e.target.value)} className="w-full px-3 py-2 bg-stone-800 rounded-lg" />
                    </label>
                    <label className="text-sm flex items-center gap-2">
                      <input type="checkbox" checked={d.is_published !== false} onChange={e => setField(inst.id, 'is_published', e.target.checked)} />
                      <span>Published (visible on /play-along)</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
